// 弹窗创建 API 项目（替代旧的整页 /workspace/new 表单入口）。
// 只收集 名称 / 介绍 / 类型 / 模板 四项，落 DRAFT 骨架草稿后跳进 workspace 慢慢配。
// 上游 baseUrl 一律不在这里填——进 workspace 的「上游接入」tab 再配（支持多环境）。
//
// 复用：模板走后端 /parse-openapi（含 tags→group），再用 register.jsx 导出的
// buildCreatePayload + mergeImportedEndpoints 拼 wire payload，不重造转换逻辑。
import React, { useState } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../ui/index.js';
import { XApi } from '../services.js';
import { XShell } from '../shell.jsx';
import { XPrim } from '../primitives.jsx';
import { useT } from '../i18n.jsx';
import {
  CATEGORIES,
  INITIAL_FORM,
  INITIAL_ENDPOINT,
  buildCreatePayload,
} from './register.jsx';
import { mergeImportedEndpoints, importedTransportSpecInput } from './workspace-sections/openapi-merge.js';

// 「空白」是前端专属选项(不进 DB)：直接建空草稿。其余模板从后端 /project-templates 拉。
const BLANK_SLUG = 'blank';

export const CreateProjectModal = ({ open, onClose }) => {
  const { navigate } = XShell.useRoute();
  const { toast } = XPrim.useToast();
  const { t } = useT();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [templateSlug, setTemplateSlug] = useState(BLANK_SLUG);
  const [templates, setTemplates] = useState([]); // 后端模板(轻量元数据)
  const [tplLoading, setTplLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // 打开弹窗时拉取后端模板列表
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTplLoading(true);
    XApi.apiSvc
      .listTemplates()
      .then((list) => { if (!cancelled) setTemplates(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setTemplates([]); })
      .finally(() => { if (!cancelled) setTplLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // 空白 + 后端模板。空白的名称/介绍走 i18n，DB 模板用各自的 name/description。
  const allTemplates = [
    { slug: BLANK_SLUG, icon: '✏️', name: t('createProject.tpl.blank'), description: t('createProject.tpl.blank.desc') },
    ...templates,
  ];

  const reset = () => {
    setName('');
    setDescription('');
    setCategory(CATEGORIES[0]);
    setTemplateSlug(BLANK_SLUG);
  };
  const close = () => {
    if (creating) return;
    reset();
    onClose && onClose();
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast(t('createProject.nameRequired'));
      return;
    }
    setCreating(true);
    try {
      let endpoints = [];
      let openApiSpec;
      if (templateSlug !== BLANK_SLUG) {
        // 选中模板 → 拉完整 openApiSpec → 后端 /parse-openapi 解析成 endpoints（含 group）
        const tpl = await XApi.apiSvc.getTemplate(templateSlug);
        const spec = tpl && tpl.openApiSpec;
        if (spec) {
          openApiSpec = importedTransportSpecInput(spec);
          const parsed = await XApi.apiSvc.parseOpenApi(JSON.stringify(spec));
          endpoints = mergeImportedEndpoints(
            [],
            (parsed && parsed.endpoints) || [],
            INITIAL_ENDPOINT,
          );
        }
      }
      const form = {
        ...INITIAL_FORM(),
        name: trimmed,
        description: description.trim(),
        category,
        endpoints,
        openApiSpec,
        baseUrl: '',
      };
      const payload = buildCreatePayload(form);
      // 骨架草稿：不带 baseUrl（空串会被 @IsUrl 拒），进 workspace 再配上游
      delete payload.baseUrl;
      const created = await XApi.apiSvc.create(payload);
      toast(t('createProject.created'));
      reset();
      onClose && onClose();
      if (created && created.id) {
        navigate('workspace-api', { apiId: created.id, section: 'basic' });
      }
    } catch (e) {
      toast((e && e.message) || t('createProject.failed'));
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onClose={close} maxWidth={640}>
      <DialogHeader title={t('createProject.title')} onClose={close} />
      <DialogBody>
        <div className="col gap-3">
          <div className="col gap-1">
            <label className="micro" style={{ color: 'var(--fg-2)' }}>
              {t('createProject.name')} <span style={{ color: 'var(--danger, #e5484d)' }}>*</span>
            </label>
            <input
              className="input"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && name.trim() && !creating) submit(); }}
              placeholder={t('createProject.name.ph')}
              maxLength={80}
            />
          </div>

          <div className="col gap-1">
            <label className="micro" style={{ color: 'var(--fg-2)' }}>
              {t('createProject.desc')}
            </label>
            <textarea
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('createProject.desc.ph')}
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="col gap-1">
            <label className="micro" style={{ color: 'var(--fg-2)' }}>
              {t('createProject.type')}
            </label>
            {/* 原生 select：Base UI 的 Select 用 Portal 弹层，嵌在 Base UI Dialog 里会被
                dialog 的 modal/inert 层挡住打不开；原生 select 在 OS 层渲染，永不被裁剪。 */}
            <select
              className="input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{ width: '100%', cursor: 'pointer', height: 36 }}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="col gap-1">
            <label className="micro" style={{ color: 'var(--fg-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
              {t('createProject.template')}
              {tplLoading && <span className="micro" style={{ color: 'var(--fg-4)' }}>· 加载中…</span>}
            </label>
            {/* apx-stagger-in: DB templates arrive async — cascade them in
                instead of popping the whole grid. */}
            <div
              key={tplLoading ? 'loading' : 'loaded'}
              className="apx-stagger-in"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 10,
              }}
            >
              {allTemplates.map((tpl) => {
                const active = tpl.slug === templateSlug;
                return (
                  <button
                    type="button"
                    key={tpl.slug}
                    onClick={() => setTemplateSlug(tpl.slug)}
                    className="tpl-card"
                    style={{
                      textAlign: 'left',
                      padding: '12px 14px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      background: active ? 'var(--accent-soft, rgba(245,165,36,0.08))' : 'var(--bg-2)',
                      border: `1px solid ${active ? 'var(--accent, #f5a524)' : 'var(--line)'}`,
                    }}
                  >
                    <div style={{ fontSize: 18, marginBottom: 4 }}>{tpl.icon || '📦'}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                      <span>{tpl.name}</span>
                      {tpl.endpointCount > 0 && (
                        <span className="micro" style={{ color: 'var(--fg-4)', textTransform: 'none', letterSpacing: 0 }}>· {tpl.endpointCount} 接口</span>
                      )}
                    </div>
                    <div className="micro" style={{ color: 'var(--fg-2)', marginTop: 2, lineHeight: 1.4, textTransform: 'none', letterSpacing: 0 }}>
                      {tpl.description}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div
            className="micro"
            style={{ color: 'var(--fg-3, var(--fg-2))', lineHeight: 1.5, textTransform: 'none', letterSpacing: 0 }}
          >
            {t('createProject.hint')}
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <button className="btn btn-ghost" onClick={close} disabled={creating}>
          {t('createProject.cancel')}
        </button>
        <button
          className="btn btn-primary"
          onClick={submit}
          disabled={creating || !name.trim()}
        >
          {creating ? t('createProject.creating') : t('createProject.submit')}
        </button>
      </DialogFooter>
    </Dialog>
  );
};

// 路由页：所有「新建 API」入口（console / workspace 列表 / market）都 navigate('workspace-new')
// 落到这里，统一弹出创建弹窗。取消 → 回到 workspace 列表；创建成功 → 弹窗内部跳 workspace-api。
export const CreateProjectPage = () => {
  const { navigate } = XShell.useRoute();
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-0)' }}>
      <CreateProjectModal open onClose={() => navigate('workspace')} />
    </div>
  );
};

export default CreateProjectModal;
